import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd, calcEst } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea, EmptyState } from '../ui.jsx';
import { ContactLink } from '../widgets/ContactQuickEdit.jsx';
import { ContactPicker } from '../widgets/ContactPicker.jsx';
import { PhotoPicker } from '../widgets/PhotoPicker.jsx';
import { PriceBookPicker } from '../widgets/PriceBookPicker.jsx';
import { ComplementaryItems, PaymentPlanPicker } from '../widgets/QuickCreateFlow.jsx';

const STATUSES = ['draft','sent','viewed','approved','declined','expired'];
const ST_COLOR = { draft:G.textMuted, sent:G.cyan, viewed:G.purple, approved:G.green, declined:G.red, expired:G.yellow };

export function Estimates({ toast }) {
  const [estimates, setEstimates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    try {
      const [e, c] = await Promise.all([api('/api/estimates'), api('/api/contacts').catch(()=>[])]);
      setEstimates(e); setContacts(c);
    } catch(err) { toast(err.message,'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const send = async est => {
    if (!confirm(`Send estimate to ${est.contact} via SMS + email? This also starts the follow-up sequence.`)) return;
    try { await api(`/api/estimates/${est.id}/send`, { method:'POST' }); load(); toast('Estimate sent + follow-up sequence enrolled'); }
    catch(e) { toast(e.message,'error'); }
  };

  const aiReview = async est => {
    try { const result = await api(`/api/estimates/${est.id}/review`, { method:'POST' }); setReviewing({ est, result }); }
    catch(e) { toast(e.message,'error'); }
  };

  const convert = async est => {
    if (!confirm('Convert this estimate to an invoice?')) return;
    try { const result = await api(`/api/estimates/${est.id}/convert`, { method:'POST' }); load(); toast(`Created invoice ${result.invoice.num}`); }
    catch(e) { toast(e.message,'error'); }
  };

  const remove = async est => {
    if (!confirm('Delete this estimate?')) return;
    try { await api(`/api/estimates/${est.id}`, { method:'DELETE' }); load(); toast('Estimate deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Estimates <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({estimates.length})</span></h1>
      <Btn v="primary" onClick={()=>setEditing({ status:'draft', taxRate:7, lines:[{name:'',qty:1,price:0,unit:'each'}], validUntil: new Date(Date.now()+30*864e5).toISOString().slice(0,10) })}>+ New Estimate</Btn>
    </div>

    {loading ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
    : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          {['#','Contact','Title','Total','Status','Valid Until','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>{estimates.map(est => {
          const { total } = calcEst(est.lines, est.taxRate);
          return <tr key={est.id}>
            <td style={{ ...styles.td, fontWeight:600 }}>{est.num}</td>
            <td style={styles.td}><ContactLink id={est.contactId}>{est.contact}</ContactLink></td>
            <td style={styles.td}>{est.title}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(total)}</td>
            <td style={styles.td}><Badge c={ST_COLOR[est.status]} bg={ST_COLOR[est.status]+'22'}>{est.status}</Badge></td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{est.validUntil || '—'}</td>
            <td style={styles.td}>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <Btn sz="xs" onClick={()=>setEditing(est)}>Edit</Btn>
                <Btn sz="xs" v="cyan" onClick={()=>aiReview(est)}>🤖 AI Review</Btn>
                {est.status === 'draft' && <Btn sz="xs" v="primary" onClick={()=>send(est)}>Send</Btn>}
                {['sent','viewed'].includes(est.status) && <Btn sz="xs" v="cyan" onClick={()=>setFollowUp(est)}>Follow Up</Btn>}
                {['sent','viewed','approved'].includes(est.status) && !est.convertedInvoiceId && <Btn sz="xs" v="green" onClick={()=>convert(est)}>→ Invoice</Btn>}
                <Btn sz="xs" v="danger" onClick={()=>remove(est)}>×</Btn>
              </div>
            </td>
          </tr>;
        })}</tbody>
      </table>
      {estimates.length === 0 && <EmptyState
        title="No estimates yet"
        body="Send your first estimate in under 2 minutes. We auto-build Good/Better/Best tiers so customers see options — and Better gets picked ~60% of the time."
        ctaLabel="+ New estimate"
        onCta={()=>setEditing({ lines:[{ name:'', qty:1, price:0, unit:'each' }] })}
      />}
    </div>}

    <EstimateModal estimate={editing} contacts={contacts} toast={toast} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Estimate saved'); }}/>
    <FollowUpModal estimate={followUp} onClose={()=>setFollowUp(null)} onSent={()=>{ load(); setFollowUp(null); toast('Follow-up sent'); }}/>
    <AIReviewModal data={reviewing} onClose={()=>{ setReviewing(null); load(); }} onApplied={load}/>
  </div>;
}

function AIReviewModal({ data, onClose, onApplied }) {
  const [issues, setIssues] = useState([]);
  const [applying, setApplying] = useState(null);
  useEffect(() => { setIssues(data?.result?.issues || []); }, [data]);
  if (!data) return null;
  const { est, result } = data;
  const sevColor = s => s==='high' ? G.red : s==='med' ? G.yellow : G.textMuted;

  const applyFix = async (issue, idx) => {
    if (!issue.fix) return;
    setApplying(idx);
    try {
      await api(`/api/estimates/${est.id}/apply-fix`, { method:'POST', body:{ fix: issue.fix }});
      setIssues(issues.map((it,i) => i===idx ? { ...it, _applied: true } : it));
      onApplied?.();
    } catch(e) { alert(e.message); }
    finally { setApplying(null); }
  };

  const applyAll = async () => {
    for (let i = 0; i < issues.length; i++) {
      if (issues[i].fix && !issues[i]._applied) await applyFix(issues[i], i);
    }
  };

  const fixableCount = issues.filter(i => i.fix && !i._applied).length;

  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 4px 0' }}>🤖 AI Estimate Review — {est.num}</h2>
    <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
      <div style={{ width:80, height:80, borderRadius:'50%', background: result.score>=80?G.greenLight:result.score>=60?G.yellowLight:G.redLight, color: result.score>=80?G.green:result.score>=60?G.yellow:G.red, display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, fontWeight:800 }}>{result.score}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:18, fontWeight:700, color: result.recommendation === 'looks_good' ? G.green : result.recommendation === 'review_first' ? G.yellow : G.red }}>{result.recommendation === 'looks_good' ? 'Looks good' : result.recommendation === 'review_first' ? 'Review first' : 'Needs work'}</div>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>{result.summary.lines} lines · ${result.summary.subtotal.toFixed(2)} subtotal · {result.summary.marginPct}% est. margin</div>
        {result.aiNotes?.summary && <div style={{ fontSize:13, color:G.purple, marginTop:6, fontStyle:'italic' }}>"{result.aiNotes.summary}"</div>}
      </div>
      {fixableCount > 0 && <Btn v="primary" sz="sm" onClick={applyAll}>⚡ Apply all {fixableCount} fixes</Btn>}
    </div>

    {issues.length === 0 ? <div style={{ padding:18, textAlign:'center', color:G.green }}>No issues found. Send with confidence!</div>
    : <div>
      <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>{issues.length} issue{issues.length>1?'s':''}</div>
      {issues.map((i,idx) => (
        <div key={idx} style={{ ...styles.card, padding:12, marginBottom:8, borderColor: i._applied ? G.green+'66' : sevColor(i.severity)+'66', background: i._applied ? G.greenLight : G.card }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                {i._applied ? <Badge c={G.green} bg={G.greenLight}>✓ applied</Badge> : <Badge c={sevColor(i.severity)} bg={sevColor(i.severity)+'22'}>{i.severity}</Badge>}
                <strong style={{ fontSize:14 }}>{i.message}</strong>
              </div>
              <div style={{ fontSize:12, color:G.purple }}>→ {i.suggestion}</div>
            </div>
            {i.fix && !i._applied && <Btn sz="sm" v="cyan" onClick={()=>applyFix(i, idx)} disabled={applying===idx}>
              {applying===idx ? <Spinner sz={12}/> : '⚡ Apply'}
            </Btn>}
          </div>
        </div>
      ))}
    </div>}

    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}><Btn onClick={onClose}>Done</Btn></div>
  </Modal>;
}

function EstimateModal({ estimate, contacts, onClose, onSaved, toast = () => {} }) {
  const [form, setForm] = useState({});
  const [priceBook, setPriceBook] = useState([]);
  const [pbOpen, setPbOpen] = useState(false);
  const [pbQuery, setPbQuery] = useState('');
  useEffect(() => { setForm(estimate || {}); }, [estimate]);
  useEffect(() => { api('/api/pricebook').then(setPriceBook).catch(()=>{}); }, []);
  if (!estimate) return null;
  const isNew = !form.id;
  const lines = form.lines || [];
  const { sub, tax, total } = calcEst(lines, form.taxRate);

  const pbFiltered = pbQuery
    ? priceBook.filter(p => `${p.name} ${p.category||''} ${p.sku||''}`.toLowerCase().includes(pbQuery.toLowerCase())).slice(0, 30)
    : priceBook.slice(0, 30);
  const addFromPriceBook = (p) => {
    setForm({ ...form, lines: [...lines, { name: p.name, qty: 1, price: Number(p.price)||0, unit: p.unit || 'each' }] });
  };

  const updateLine = (i, k, v) => {
    const nl = lines.slice();
    nl[i] = { ...nl[i], [k]: k==='name'||k==='unit' ? v : (Number(v)||0) };
    setForm({ ...form, lines: nl });
  };
  const addLine = () => setForm({ ...form, lines: [...lines, { name:'', qty:1, price:0, unit:'each' }] });
  const delLine = i => setForm({ ...form, lines: lines.filter((_,j)=>j!==i) });

  const save = async () => {
    try {
      if (isNew) await api('/api/estimates', { method:'POST', body: form });
      else await api(`/api/estimates/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={840}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew ? 'New Estimate' : `${form.num} — ${form.title || ''}`}</h2>
    {/* Customer summary — shows the address/phone/email the moment a contact is picked
        so the owner can verify the data that'll appear on the customer-facing estimate. */}
    {form.contactId && (() => {
      const picked = contacts.find(x => x.id === form.contactId);
      if (!picked) return null;
      return <div style={{ ...styles.card, padding:'10px 14px', marginBottom:14, background:G.input, fontSize:12 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>Customer info on this estimate</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:6, color:G.textMuted }}>
          {picked.phone && <div>{picked.phone}</div>}
          {picked.email && <div>{picked.email}</div>}
          {picked.address && <div>{[picked.address, picked.city, picked.state, picked.zip].filter(Boolean).join(', ')}</div>}
          {picked.stage && <div>Stage: <strong style={{color:G.text}}>{picked.stage}</strong></div>}
        </div>
      </div>;
    })()}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Contact">
        <ContactPicker
          value={form.contactId}
          onChange={(c) => setForm({ ...form, contactId: c.id, contact: c.id ? `${c.firstName||''} ${c.lastName||''}`.trim() : '' })}
        />
      </Field>
      <Field label="Title"><Input value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="AC Replacement"/></Field>
      <Field label="Tax Rate %"><Input type="number" step="0.1" value={form.taxRate||7} onChange={e=>setForm({...form,taxRate:Number(e.target.value)})}/></Field>
      <Field label="Valid Until"><Input type="date" value={form.validUntil||''} onChange={e=>setForm({...form,validUntil:e.target.value})}/></Field>
      <Field label="Payment Terms" hint="Used when this estimate converts to an invoice on approval.">
        <select value={form.paymentTerms || 'Net 30'} onChange={e=>setForm({...form,paymentTerms:e.target.value})} style={{ ...styles.inp, width:'100%' }}>
          {['Due on receipt','Net 7','Net 14','Net 30','Net 60'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Offer Financing on this estimate" style={{ gridColumn:'1/-1' }}>
        <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background: form.financingOffered !== false ? G.greenLight : G.input, border:`1px solid ${form.financingOffered !== false ? G.green+'66' : G.cardBorder}`, borderRadius:8, cursor:'pointer' }}>
          <input type="checkbox" checked={form.financingOffered !== false} onChange={e=>setForm({...form, financingOffered: e.target.checked})}/>
          <span style={{ fontSize:13 }}>{form.financingOffered !== false ? '"Finance this" button shown to customer' : 'Hidden — no financing button on this estimate'}</span>
        </label>
      </Field>
    </div>

    <GoodBetterBest form={form} setForm={setForm}/>

    <div style={{ marginTop:8 }}>
      <div style={{ fontSize:12, fontWeight:600, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>{form.tiers?.length ? 'Baseline Line Items (used as "Good" tier)' : 'Line Items'}</div>
      <div style={{ ...styles.card, padding:12 }}>
        <table style={{ width:'100%' }}>
          <thead><tr style={{ fontSize:11, color:G.textMuted, textAlign:'left' }}>
            <th style={{ paddingBottom:6 }}>Description</th>
            <th style={{ paddingBottom:6, width:80 }}>Qty</th>
            <th style={{ paddingBottom:6, width:110 }}>Price</th>
            <th style={{ paddingBottom:6, width:80 }}>Unit</th>
            <th style={{ paddingBottom:6, width:110 }}>Total</th>
            <th style={{ paddingBottom:6, width:30 }}></th>
          </tr></thead>
          <tbody>{lines.map((l, i) => (
            <tr key={i}>
              <td>
                {/* Type to search Price Book — picking an item auto-fills price + unit */}
                <PriceBookPicker
                  value={l.name}
                  onPick={(picked) => {
                    if (picked.price != null) {
                      // Came from price book — fill all matching fields
                      const nl = [...lines];
                      nl[i] = { ...l, name: picked.name, price: picked.price, qty: picked.qty || l.qty || 1, unit: picked.unit || l.unit || 'each' };
                      setForm({ ...form, lines: nl });
                    } else {
                      updateLine(i, 'name', picked.name);
                    }
                  }}
                />
              </td>
              <td><Input type="number" value={l.qty||0} onChange={e=>updateLine(i,'qty',e.target.value)} /></td>
              <td><Input type="number" step="0.01" value={l.price||0} onChange={e=>updateLine(i,'price',e.target.value)} /></td>
              <td><Input value={l.unit||'each'} onChange={e=>updateLine(i,'unit',e.target.value)} /></td>
              <td style={{ padding:'0 8px', fontWeight:600 }}>{fmtUsd((Number(l.qty)||0)*(Number(l.price)||0))}</td>
              <td><Btn sz="xs" v="danger" onClick={()=>delLine(i)}>×</Btn></td>
            </tr>
          ))}</tbody>
        </table>
        <div style={{ display:'flex', gap:6, marginTop:8 }}>
          <Btn sz="sm" onClick={addLine}>+ Blank Line</Btn>
          <Btn sz="sm" v="cyan" onClick={()=>setPbOpen(!pbOpen)}>📚 From Price Book ({priceBook.length})</Btn>
        </div>
        <div style={{ marginTop:10 }}>
          <ComplementaryItems
            title={form.title}
            lines={lines}
            setLines={(nl) => setForm({ ...form, lines: nl })}
            toast={toast}
          />
        </div>
        {pbOpen && <div style={{ marginTop:10, padding:12, background:G.input, borderRadius:8, maxHeight:280, overflow:'auto' }}>
          <Input value={pbQuery} onChange={e=>setPbQuery(e.target.value)} placeholder="Search price book..." style={{ marginBottom:8 }}/>
          {pbFiltered.length === 0 && <div style={{ color:G.textMuted, fontSize:13, padding:8 }}>No items{priceBook.length===0 ? ' — add some in Price Book first' : ' match that search'}.</div>}
          {pbFiltered.map(p => (
            <div key={p.id} onClick={()=>addFromPriceBook(p)} style={{ padding:'8px 10px', cursor:'pointer', borderRadius:6, display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:`1px solid ${G.cardBorder}` }}
              onMouseEnter={e=>e.currentTarget.style.background=G.purpleDim}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{p.name}</div>
                <div style={{ fontSize:11, color:G.textMuted }}>{p.category || 'No category'}{p.sku?` · ${p.sku}`:''}{p.unit?` · ${p.unit}`:''}</div>
              </div>
              <div style={{ fontWeight:700, color:G.green }}>{fmtUsd(p.price||0)}</div>
            </div>
          ))}
        </div>}
      </div>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:30, marginTop:14, fontSize:14 }}>
      <div style={{ textAlign:'right' }}>
        <div style={{ color:G.textMuted, marginBottom:4 }}>Subtotal: <strong style={{ color:G.text }}>{fmtUsd(sub)}</strong></div>
        <div style={{ color:G.textMuted, marginBottom:4 }}>Tax: <strong style={{ color:G.text }}>{fmtUsd(tax)}</strong></div>
        <div style={{ fontSize:18, fontWeight:800, color:G.green }}>Total: {fmtUsd(total)}</div>
      </div>
    </div>

    <PaymentPlanPicker total={total} plan={form.paymentPlan || { type:'in_full' }} setPlan={p => setForm({ ...form, paymentPlan: p })}/>

    <Field label="Notes (visible to customer)"><Textarea value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})}/></Field>

    {/* Photos for the estimate — before pictures, problem shots, reference. Customer sees these on the estimate page. */}
    <Field label="Photos">
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <PhotoPicker label="📷 Add Photo" upload multiple onPicked={(uploaded) => {
          const list = Array.isArray(uploaded) ? uploaded : [uploaded];
          const existing = Array.isArray(form.photos) ? form.photos : [];
          setForm({...form, photos: [...existing, ...list.filter(Boolean).map(u => ({ url: u.url, name: u.name }))]});
        }}/>
        {(form.photos||[]).length > 0 && <span style={{ fontSize:12, color:G.textMuted }}>{(form.photos||[]).length} photo{(form.photos||[]).length===1?'':'s'}</span>}
      </div>
      {(form.photos||[]).length > 0 && <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(90px, 1fr))', gap:6, marginTop:10 }}>
        {form.photos.map((p, i) => (
          <div key={i} style={{ position:'relative', borderRadius:6, overflow:'hidden', background:G.input, aspectRatio:'1' }}>
            <img src={p.url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            <button onClick={()=>setForm({...form, photos: form.photos.filter((_,j)=>j!==i)})} style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,.6)', color:'#fff', border:'none', borderRadius:'50%', width:20, height:20, cursor:'pointer', fontSize:11 }}>×</button>
          </div>
        ))}
      </div>}
    </Field>
    <Field label="Internal Notes"><Textarea value={form.internalNotes||''} onChange={e=>setForm({...form,internalNotes:e.target.value})}/></Field>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function FollowUpModal({ estimate, onClose, onSent }) {
  const [channel, setChannel] = useState('both');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  if (!estimate) return null;
  const send = async () => {
    setBusy(true);
    try { await api(`/api/estimates/${estimate.id}/follow-up`, { method:'POST', body:{ channel, message: message||null } }); onSent(); }
    catch(e) { alert(e.message); setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 4px 0' }}>Send Follow-Up</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>To: <strong style={{ color:G.text }}>{estimate.contact}</strong> · Estimate: <strong style={{ color:G.text }}>{estimate.num}</strong></div>
    <Field label="Channel">
      <Select value={channel} onChange={e=>setChannel(e.target.value)}>
        <option value="both">SMS + Email</option>
        <option value="sms">SMS only</option>
        <option value="email">Email only</option>
      </Select>
    </Field>
    <Field label="Message (optional — leave blank for default)" hint="Default: friendly check-in with link to view the estimate again.">
      <Textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Hey {first_name}, just checking in on the estimate..."/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy}>{busy?<Spinner/>:'Send Follow-Up'}</Btn>
    </div>
  </Modal>;
}

function GoodBetterBest({ form, setForm }) {
  const [busy, setBusy] = useState(false);
  // Every estimate ALWAYS has Good/Better/Best. If tiers are missing (legacy
  // estimate from before the universal-tiers change, or a brand-new in-memory
  // form), seed three empty placeholders the user can fill in.
  const baselineLines = form.lines || [];
  // Differentiate the seed so customers see real Good/Better/Best contrast
  // even before the owner clicks "AI Rebuild". Good = baseline as-is, Better
  // adds warranty + tune-up, Best adds premium upgrade on top.
  const baselineSub = baselineLines.reduce((s,l)=>s+(Number(l.qty)||1)*(Number(l.price)||0),0);
  const warrantyLine    = { name:'Extended 5-year warranty (parts & labor)', qty:1, price:Math.max(149, Math.round(baselineSub*0.08)), unit:'each' };
  const tuneUpLine      = { name:'Annual maintenance tune-up (2 visits/yr)', qty:1, price:199, unit:'each' };
  const premiumLine     = { name:'Premium-tier upgrade (top brand)',         qty:1, price:Math.max(199, Math.round(baselineSub*0.20)), unit:'each' };
  const prioritySupport = { name:'Priority same-day service for 12 months',  qty:1, price:299, unit:'each' };
  // Detect legacy/identical tiers (all three have the same lines) and re-seed
  // with the differentiated default so the customer-facing view always shows
  // a real contrast.
  const sig = (t) => JSON.stringify((t.lines||[]).map(l=>[l.name, Number(l.qty)||1, Number(l.price)||0]));
  const tiersIdentical = form.tiers && form.tiers.length === 3 && sig(form.tiers[0]) === sig(form.tiers[1]) && sig(form.tiers[1]) === sig(form.tiers[2]);
  const tiers = (form.tiers && form.tiers.length === 3 && !tiersIdentical) ? form.tiers : [
    { key:'good',   name:'Good',   label:'Reliable basics',      blurb:'Gets the job done — quality install with our standard 1-year workmanship warranty.', lines: baselineLines.map(l=>({...l})) },
    { key:'better', name:'Better', label:'Most popular',         blurb:'Everything in Good, plus a 5-year warranty and yearly maintenance so you forget about it.', lines: [...baselineLines.map(l=>({...l})), warrantyLine, tuneUpLine] },
    { key:'best',   name:'Best',   label:'Worry-free for years', blurb:'Top-of-the-line equipment with priority service and full coverage — peace of mind, locked in.', lines: [...baselineLines.map(l=>({...l})), premiumLine, warrantyLine, tuneUpLine, prioritySupport] },
  ];
  // Persist the seed back to form state on first render so saves include it.
  React.useEffect(() => {
    if (!form.tiers || form.tiers.length !== 3 || tiersIdentical) setForm({ ...form, tiers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AI tier generation works in two modes:
  //  - Estimate already saved → POST /api/estimates/:id/generate-tiers (writes tiers to DB)
  //  - Estimate not yet saved → POST /api/estimates/generate-tiers-preview (in-memory only)
  // Either way the result lands on form.tiers so the user can review + tweak
  // before the next save flushes everything to the DB.
  const generate = async () => {
    const lines = (form.lines || []).filter(l => l.name && Number(l.price));
    if (!lines.length) { alert('Add at least one line item to use AI tier generation.'); return; }
    setBusy(true);
    try {
      const r = form.id
        ? await api(`/api/estimates/${form.id}/generate-tiers`, { method:'POST' })
        : await api(`/api/estimates/generate-tiers-preview`, { method:'POST', body:{ title: form.title || '', lines, taxRate: form.taxRate || 7 }});
      setForm({ ...form, tiers: r.tiers });
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const updateTierLine = (tIdx, lIdx, k, v) => {
    const nextTiers = tiers.map((t, i) => {
      if (i !== tIdx) return t;
      const nl = (t.lines || []).slice();
      nl[lIdx] = { ...nl[lIdx], [k]: k==='name'||k==='unit' ? v : (Number(v)||0) };
      return { ...t, lines: nl };
    });
    setForm({ ...form, tiers: nextTiers });
  };

  const addLineToTier = (tIdx) => {
    const nextTiers = tiers.map((t,i) => i!==tIdx ? t : { ...t, lines: [...(t.lines||[]), { name:'', qty:1, price:0, unit:'each' }] });
    setForm({ ...form, tiers: nextTiers });
  };

  const delLineFromTier = (tIdx, lIdx) => {
    const nextTiers = tiers.map((t,i) => i!==tIdx ? t : { ...t, lines: (t.lines||[]).filter((_,j)=>j!==lIdx) });
    setForm({ ...form, tiers: nextTiers });
  };

  return <div style={{ ...styles.card, padding:16, marginTop:14, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
      <div>
        <div style={{ fontSize:15, fontWeight:700 }}>Good / Better / Best</div>
        <div style={{ fontSize:12, color:G.textMuted }}>Every estimate ships with three options — customer picks one to convert it into an invoice.</div>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <Btn sz="sm" v="ghost" onClick={generate} disabled={busy}>{busy ? <Spinner sz={12}/> : 'AI Rebuild'}</Btn>
      </div>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
      {tiers.map((t, tIdx) => {
        const total = (t.lines||[]).reduce((s,l)=>s+(Number(l.qty)||1)*(Number(l.price)||0),0);
        return <div key={t.key||tIdx} style={{ background:G.input, borderRadius:10, padding:12, border:`1px solid ${tIdx===1?G.purple:G.cardBorder}`, position:'relative' }}>
          {tIdx===1 && <div style={{ position:'absolute', top:-9, left:'50%', transform:'translateX(-50%)', background:G.purple, color:'#fff', fontSize:10, padding:'2px 8px', borderRadius:99, fontWeight:700 }}>MOST POPULAR</div>}
          <Input value={t.name||''} onChange={e=>{ const nt=[...tiers]; nt[tIdx]={...t,name:e.target.value}; setForm({...form,tiers:nt}); }} style={{ fontWeight:700, fontSize:15 }}/>
          <Input value={t.label||''} placeholder="One-line subtitle" onChange={e=>{ const nt=[...tiers]; nt[tIdx]={...t,label:e.target.value}; setForm({...form,tiers:nt}); }} style={{ fontSize:12, marginTop:4 }}/>
          <Textarea value={t.blurb||''} placeholder="Why this tier" onChange={e=>{ const nt=[...tiers]; nt[tIdx]={...t,blurb:e.target.value}; setForm({...form,tiers:nt}); }} style={{ fontSize:11, marginTop:4, minHeight:48 }}/>
          <div style={{ fontWeight:800, fontSize:20, marginTop:8 }}>{fmtUsd(total)}</div>
          <div style={{ marginTop:8, fontSize:11 }}>
            {(t.lines||[]).map((l, lIdx) => (
              <div key={lIdx} style={{ display:'grid', gridTemplateColumns:'1fr 50px 70px 20px', gap:4, marginBottom:4 }}>
                <Input style={{ fontSize:11, padding:'4px 6px' }} value={l.name||''} onChange={e=>updateTierLine(tIdx,lIdx,'name',e.target.value)} placeholder="Item"/>
                <Input style={{ fontSize:11, padding:'4px 6px' }} type="number" value={l.qty||0} onChange={e=>updateTierLine(tIdx,lIdx,'qty',e.target.value)}/>
                <Input style={{ fontSize:11, padding:'4px 6px' }} type="number" value={l.price||0} onChange={e=>updateTierLine(tIdx,lIdx,'price',e.target.value)}/>
                <Btn sz="xs" v="ghost" onClick={()=>delLineFromTier(tIdx,lIdx)}>×</Btn>
              </div>
            ))}
            <Btn sz="xs" v="ghost" onClick={()=>addLineToTier(tIdx)} style={{ marginTop:4 }}>+ line</Btn>
          </div>
        </div>;
      })}
    </div>
  </div>;
}
